import React, {
  // FC,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { navigate } from 'gatsby';
import { createPortal } from 'react-dom';
import cx from 'classnames';

import {
  Button,
  IconButton,
  Modal,
  Text,
} from 'components/UI';
import useMediaQuery from 'utils/useMediaQuery';

import themeVars from 'theme/vars.scss';

import styles from './filters.module.scss';

interface IFilterValue {
  value: string;
  label: string;
}
export interface IFilter<T> {
  key: keyof T;
  label: string;
  allLabel: string;
  values: IFilterValue[];
}

interface IProps<T> {
  appearance?: 'compact';
  toggleId: string;
  data: IFilter<T>[];
  selection: T;
  onSelect?: (payload: T) => void;
  linkRenderer?: (payload: T) => string;
}

function Filters<T extends { [key: string]: string | undefined }>({
  appearance,
  toggleId,
  data,
  selection,
  onSelect,
  linkRenderer,
}: IProps<T>) {
  const className = cx(
    styles.filter,
    {
      [styles.filterCompact]: appearance === 'compact',
    },
  );
  const isVisible = useMediaQuery(`(min-width: ${themeVars.mqSm})`);

  const [isOpened, setIsOpened] = useState(false);
  const [toggleRoot, setToggleRoot] = React.useState<Element | null>(null);

  const [currentSelection, setCurrentSelection] = useState<T>({ ...selection });

  useEffect(() => {
    setToggleRoot(document.getElementById(toggleId));
  }, []);

  useEffect(() => {
    if (isVisible) {
      setCurrentSelection({ ...selection });
    }
  }, [isVisible, selection]);

  /* handlers */

  const handleSelect = (key: keyof T, value?: string) => () => {
    const newSelection: T = {
      ...currentSelection,
      [key]: value,
    };
    setCurrentSelection(newSelection);
    if (!isVisible) {
      return;
    }
    if (onSelect) onSelect(newSelection);
  };

  const handleApply = useCallback(() => {
    if (linkRenderer) {
      navigate(linkRenderer(currentSelection));
      return;
    }
    if (onSelect) {
      onSelect(currentSelection);
    }
    setIsOpened(false);
  }, [currentSelection]);

  const handleOpen = useCallback(() => {
    setCurrentSelection({ ...selection });
    setIsOpened(true);
  }, [selection]);

  const handleClose = useCallback(() => {
    setIsOpened(false);
  }, []);

  /* renderers */

  const renderLink = (key: keyof T, value?: string) => {
    if (!isVisible || !linkRenderer) return undefined;
    return linkRenderer({
      ...currentSelection,
      [key]: value,
    });
  };

  const renderContent = () => (
    <div className={styles.content}>
      {data.map(({ key, allLabel, values }: IFilter<T>) => (
        <div
          key={key as string}
          className={className}
        >
          <Button
            key={`${key}-all`}
            className={styles.filterValue}
            size="compact"
            link={renderLink(key)}
            appearance={!currentSelection[key] ? 'light' : 'blank'}
            isHoverDisabled={!currentSelection[key]}
            onClick={handleSelect(key)}
          >
            {allLabel}
          </Button>
          {values.map(({ value, label }: IFilterValue) => (
            <Button
              key={`${key}-${value}`}
              className={styles.filterValue}
              size="compact"
              link={renderLink(key, value)}
              appearance={currentSelection[key] === value ? 'light' : 'blank'}
              isHoverDisabled={currentSelection[key] === value}
              onClick={handleSelect(key, value)}
            >
              {label}
            </Button>
          ))}
        </div>
      ))}
    </div>
  );

  const renderToggle = () => (
    !toggleRoot ? null : createPortal((
      <IconButton
        id="filter-toggle-button"
        className={styles.buttonToggle}
        name="filters"
        onClick={handleOpen}
      />
    ), toggleRoot)
  );

  const renderFilters = () => {
    if (!isVisible) {
      return (
        <>
          <Modal
            id="filters-modal"
            position="bottom"
            isOpened={isOpened}
            onClose={handleClose}
          >
            <>
              <Text category="h3-serif">Filters</Text>
              {renderContent()}
              <Button
                className={styles.buttonApply}
                onClick={handleApply}
              >
                Apply Filters
              </Button>
            </>
          </Modal>
          {renderToggle()}
        </>
      );
    }

    return renderContent();
  };

  return (
    <div className={styles.wrapper}>
      {renderFilters()}
    </div>
  );
}

export default Filters;
